import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Crown, Building2, MessageCircle, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabaseClient";
import { handleProPlanClick } from "@/lib/checkout";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckoutModal } from "@/components/checkout/CheckoutModal";

interface Props {
  onSelectPlan: (plan: "starter" | "pro" | "enterprise") => void;
  onBack: () => void;
}

const PLANS = [
  {
    id: "starter" as const,
    name: "Starter",
    price: "Grátis",
    period: "",
    icon: Check,
    features: [
      "Score de condição do veículo",
      "Valor estimado com base na FIPE",
      "Recomendação básica",
      "1 simulação salva",
    ],
    cta: "Começar Grátis",
    highlighted: false,
  },
  {
    id: "pro" as const,
    name: "Pro",
    price: "R$ 49",
    period: "/mês",
    icon: Crown,
    features: [
      "Tudo do Starter +",
      "Análise detalhada por região",
      "Mapa de calor completo",
      "Custo estimado de reparo",
      "Histórico FIPE 12 meses",
      "Download PDF profissional",
      "Simulações ilimitadas",
    ],
    cta: "Assinar Pro",
    highlighted: true,
  },
  {
    id: "enterprise" as const,
    name: "Enterprise",
    price: "Custom",
    period: "",
    icon: Building2,
    features: [
      "Tudo do Pro +",
      "API de integração",
      "Dashboard personalizado",
      "Suporte prioritário",
      "Volume ilimitado",
    ],
    cta: "Falar com Vendas",
    highlighted: false,
  },
] as const;

export function StepPlano({ onSelectPlan, onBack }: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);

  async function handleSelect(planId: "starter" | "pro" | "enterprise") {
    if (planId === "enterprise") {
      window.open(
        "https://wa.me/5511999999999?text=Olá! Tenho interesse no plano Enterprise do AutoCarBid.",
        "_blank"
      );
      return;
    }

    setLoading(planId);

    // Check if user is logged in
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      // Save intent and redirect to signup
      sessionStorage.setItem("autocarbid_plan_intent", planId);
      toast.info("Crie sua conta para continuar");
      navigate("/cadastro");
      return;
    }

    if (planId === "pro") {
      setShowCheckout(true);
      setLoading(null);
      return;
    }

    // Starter plan - proceed directly
    onSelectPlan(planId);
    setLoading(null);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-12 max-w-3xl mx-auto"
    >
      <div className="space-y-4 text-center">
        <h2 className="text-4xl font-heading font-black tracking-tighter uppercase">Desbloqueio Analítico</h2>
        <p className="text-lg text-muted-foreground font-medium">
          Escolha o nível de profundidade da sua próxima arrematação.
        </p>
      </div>

      <div className="flex flex-col gap-6">
        {/* PRO PLAN (Main Focus) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="relative bg-primary text-primary-foreground p-8 sm:p-10 border-4 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(255,255,255,0.2)] flex flex-col md:flex-row gap-8 items-center justify-between overflow-hidden group"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none group-hover:scale-110 transition-transform duration-1000"></div>

          <div className="relative z-10 space-y-4 md:w-1/2">
            <div className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-primary-foreground" />
              <h3 className="font-heading font-black text-3xl uppercase tracking-widest">
                AutoCar PRO
              </h3>
            </div>

            <ul className="space-y-3">
              {PLANS.find(p => p.id === "pro")?.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm font-medium">
                  <Check className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative z-10 md:w-1/2 flex flex-col items-center md:items-end justify-center w-full gap-5 border-t md:border-t-0 md:border-l border-primary-foreground/30 pt-6 md:pt-0 md:pl-8 text-center md:text-right">
            <div>
              <span className="text-5xl font-heading font-black tracking-tighter block leading-none">R$ 49</span>
              <span className="text-sm uppercase tracking-widest font-bold opacity-80 mt-1 block">/mês</span>
            </div>
            <Button
              onClick={() => handleSelect("pro")}
              size="xl"
              variant="secondary"
              className="w-full text-lg font-bold shadow-xl hover:scale-105 transition-transform"
              disabled={loading !== null}
            >
              {loading === "pro" ? "Processando..." : "Desbloquear Pro Agora"}
            </Button>
          </div>
        </motion.div>

        {/* STARTER & ENTERPRISE (Secondary Options) */}
        <div className="grid md:grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="border-2 border-border/50 bg-background/50 hover:bg-muted/30 p-6 flex flex-col gap-4 transition-colors"
          >
            <div>
              <h3 className="font-heading font-bold text-xl uppercase tracking-tight">Starter</h3>
              <p className="text-sm text-muted-foreground mt-1">Apenas a análise do risco base.</p>
            </div>
            <Button
              onClick={() => handleSelect("starter")}
              variant="outline"
              className="mt-auto w-full group/btn"
              disabled={loading !== null}
            >
              {loading === "starter" ? "Processando..." : (
                <>Continuar Grátis <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" /></>
              )}
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="border-2 border-border/50 bg-background/50 hover:bg-muted/30 p-6 flex flex-col gap-4 transition-colors"
          >
            <div>
              <h3 className="font-heading font-bold text-xl uppercase tracking-tight">Enterprise</h3>
              <p className="text-sm text-muted-foreground mt-1">Integração API e alto volume corporativo.</p>
            </div>
            <Button
              onClick={() => handleSelect("enterprise")}
              variant="ghost"
              className="mt-auto w-full border border-transparent hover:border-border"
              disabled={loading !== null}
            >
              Falar com Vendas <MessageCircle className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </div>

      <div className="flex justify-center pt-4">
        <Button variant="ghost" size="lg" onClick={onBack} className="text-muted-foreground hover:text-foreground">
          Voltar ao resultado
        </Button>
      </div>

      <CheckoutModal open={showCheckout} onOpenChange={setShowCheckout} />
    </motion.div>
  );
}
